import { getExchangeRateToHKD } from '@/utils/exchangeRate.util'
import { toNumber } from '@/utils/stringHelper'
import { ApprovalStatus, Currency, PRApprovalType, PRStatus } from '@eapproval/offer-shared'

export const getGCMaterialCurrency = (row) => row?.detail?.currency ?? row?.currency ?? Currency.HKD

const firstPositiveAmount = (candidates) => {
  for (const candidate of candidates) {
    const amount = toNumber(candidate)
    if (Number.isFinite(amount) && amount > 0) {
      return amount
    }
  }

  return 0
}

export const getGCPRRowUnitCost = (row, exchangeRateMap) => {
  const master = row?.freezedDetail ?? row?.detail
  const exchangeRate = getExchangeRateToHKD(getGCMaterialCurrency(row), exchangeRateMap)

  return firstPositiveAmount([
    row?.unitCostHKD,
    row?.manualUnitCostHKD,
    master?.unitCostHKD,
    toNumber(master?.unitCost) * exchangeRate,
    toNumber(row?.manualUnitCost) * exchangeRate,
    toNumber(row?.unitCost) * exchangeRate,
  ])
}

export const getGCPRApproverDetails = ({
  requestedDetails = [],
  defaultTPMUser,
  defaultCPMUser,
  defaultLicenseManagerUser,
  defaultLogisticUser,
  requiredTPMApproval = false,
}) => {
  if (requestedDetails.length === 0) {
    throw new Error('Please fill at least 1 row')
  }
  const defaultDueDate = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)

  const approverDetails = []

  const requiredLogisticApproval = requestedDetails.some(
    (requestedDetail) =>
      !requestedDetail?.detail ||
      requestedDetail?.detail?.SAPMaterialNumber === '' ||
      requestedDetail?.detail?.discontinued
  )

  const requiredLicenseManagerApproval = requestedDetails.some(
    (requestedDetail) => requestedDetail?.detail?.requiredLicenseManager
  )

  if (requiredLogisticApproval) {
    approverDetails.push({
      status: ApprovalStatus.UPCOMING,
      user: defaultLogisticUser?._id ?? defaultLogisticUser,
      type: PRApprovalType.LOGISTIC,
      dueDate: defaultDueDate,
      remark: '',
      attachments: [],
    })
  }

  if (requiredTPMApproval) {
    approverDetails.push({
      status: ApprovalStatus.UPCOMING,
      user: defaultTPMUser?._id ?? defaultTPMUser,
      type: PRApprovalType.TECHNICAL_PROJECT_MANAGER,
      dueDate: defaultDueDate,
      remark: '',
      attachments: [],
    })
  }

  if (requiredLicenseManagerApproval) {
    approverDetails.push({
      status: ApprovalStatus.UPCOMING,
      user: defaultLicenseManagerUser?._id ?? defaultLicenseManagerUser,
      type: PRApprovalType.LICENSE_MANAGER,
      dueDate: defaultDueDate,
      remark: '',
      attachments: [],
    })
  }

  approverDetails.push({
    status: ApprovalStatus.UPCOMING,
    user: defaultCPMUser?._id ?? defaultCPMUser,
    type: PRApprovalType.COMMERCIAL_PROJECT_MANAGER,
    dueDate: defaultDueDate,
    remark: '',
    attachments: [],
  })
  approverDetails.push({
    status: ApprovalStatus.UPCOMING,
    user: defaultLogisticUser?._id ?? defaultLogisticUser,
    type: PRApprovalType.LOGISTIC,
    dueDate: defaultDueDate,
    remark: '',
    attachments: [],
  })

  return approverDetails
}

export const normalizeGCPRSubmissionForApi = (submissionDetail, { includeFilledBuyNow = false } = {}) => {
  const formattedSubmissionDetail = { ...submissionDetail }
  const getId = (value) => value?.id || value?._id || value

  if (formattedSubmissionDetail?.raiser?._id) {
    formattedSubmissionDetail.raiser = formattedSubmissionDetail.raiser._id
  }

  if (formattedSubmissionDetail?.requestor?._id) {
    formattedSubmissionDetail.requestor = formattedSubmissionDetail.requestor._id
  }

  if (includeFilledBuyNow) {
    if (formattedSubmissionDetail?.filledBuyNow?.id) {
      formattedSubmissionDetail.filledBuyNow = formattedSubmissionDetail.filledBuyNow.id
    }
    if (formattedSubmissionDetail?.filledBuyNow?._id) {
      formattedSubmissionDetail.filledBuyNow = formattedSubmissionDetail.filledBuyNow._id
    }
  }

  if (Array.isArray(formattedSubmissionDetail.internalDocument)) {
    formattedSubmissionDetail.internalDocument = formattedSubmissionDetail.internalDocument.map(
      (doc) => doc?.id || doc?._id || doc
    )
  }

  if (Array.isArray(formattedSubmissionDetail.licenseFile)) {
    formattedSubmissionDetail.licenseFile = formattedSubmissionDetail.licenseFile.map((doc) => getId(doc))
  }

  if (Array.isArray(formattedSubmissionDetail.approverDetails)) {
    formattedSubmissionDetail.approverDetails = formattedSubmissionDetail.approverDetails.map((approverDetail) => ({
      ...approverDetail,
      delegationDetail: approverDetail?.delegationDetail?._id
        ? approverDetail.delegationDetail._id
        : approverDetail?.delegationDetail,
      invitedUsers: Array.isArray(approverDetail?.invitedUsers)
        ? approverDetail.invitedUsers.map((invitedUser) => ({
            ...invitedUser,
            user: invitedUser?.user ? getId(invitedUser.user) : invitedUser?.user,
            files: Array.isArray(invitedUser?.files)
              ? invitedUser.files.map((file) => getId(file))
              : invitedUser?.files,
            replyFiles: Array.isArray(invitedUser?.replyFiles)
              ? invitedUser.replyFiles.map((file) => getId(file))
              : invitedUser?.replyFiles,
          }))
        : approverDetail?.invitedUsers,
    }))
  }

  if (Array.isArray(formattedSubmissionDetail.requestedDetails)) {
    formattedSubmissionDetail.requestedDetails = formattedSubmissionDetail.requestedDetails.map((requestedDetail) => {
      const normalizedDetail = { ...requestedDetail }

      if (normalizedDetail?.detail?.id) {
        normalizedDetail.detail = normalizedDetail.detail.id
      }
      if (normalizedDetail?.detail?._id) {
        normalizedDetail.detail = normalizedDetail.detail._id
      }

      if (Array.isArray(normalizedDetail.allDetails)) {
        normalizedDetail.allDetails = normalizedDetail.allDetails.map((d) => d?.id || d?._id || d)
      }

      return normalizedDetail
    })
  }

  return formattedSubmissionDetail
}

export const calculateGCPRSubmissionTotalAmount = (requestedDetails, exchangeRateMap) => {
  if (!Array.isArray(requestedDetails)) {
    return 0
  }

  return requestedDetails.reduce((total, requestedDetail) => {
    const unitCost = getGCPRRowUnitCost(requestedDetail, exchangeRateMap)
    const quantity = toNumber(requestedDetail?.quantity ?? 0)
    return total + unitCost * quantity
  }, 0)
}

export const getGCPRStatusBadgeColor = (status) => {
  switch (status) {
    case PRStatus.DRAFT:
      return 'yellow'
    case PRStatus.SUBMITTED:
      return 'blue'
    case PRStatus.APPROVED:
      return 'green'
    case PRStatus.REJECTED:
      return 'red'
    default:
      return 'gray'
  }
}
